import { useState, useEffect } from 'react';
import { ArConnectWallet } from '../types';

interface WalletConnectionProps {
  arConnectState: {
    isConnected: boolean;
    arweaveAddress: string | null;
    evmAddress: string | null;
    isLoading: boolean;
    error: string | null;
    isArConnectAvailable: boolean | ArConnectWallet;
    connectWallet: () => Promise<void>;
    disconnectWallet: () => Promise<void>;
    linkEvmAddress: (evmAddress: string) => Promise<boolean>;
    checkExistingLink: () => Promise<void>;
    clearError: () => void;
  };
  onConnectionChange?: (isConnected: boolean) => void;
  onContinue?: () => void;
  showContinueButton?: boolean;
}

export const WalletConnection: React.FC<WalletConnectionProps> = ({
  arConnectState,
  onConnectionChange,
  onContinue,
  showContinueButton
}) => {
  const [mounted, setMounted] = useState(false);

  const {
    isConnected,
    arweaveAddress,
    isLoading,
    error,
    isArConnectAvailable,
    connectWallet,
    disconnectWallet,
    clearError
  } = arConnectState;

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleConnect = async () => {
    clearError();
    await connectWallet();
    onConnectionChange?.(true);
  };

  const handleDisconnect = async () => {
    await disconnectWallet();
    onConnectionChange?.(false);
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  if (!mounted) {
    return (
      <div className="glass rounded-xl p-6 holo-border">
        <div className="text-center">
          <div>
            <div className="h-4 bg-cyan-400/30 rounded w-3/4 mx-auto mb-2"></div>
            <div className="h-4 bg-purple-500/30 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show detecting wallet state while checking
  if (!isArConnectAvailable && mounted) {
    return (
      <div className="glass rounded-xl p-6 holo-border">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-cyan-400/20 rounded-full flex items-center justify-center border border-cyan-500/30 glow-primary">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
          </div>
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Detecting Wallet</h3>
          <p className="text-gray-400 leading-relaxed">
            Looking for ArConnect wallet extension...
          </p>
        </div>
      </div>
    );
  }

  if (!isArConnectAvailable) {
    return (
      <div className="glass rounded-xl p-6 border border-red-500/30">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold">
              Arweave wallet not found
            </h3>
            <div className="mt-2 text-gray-300">
              <p className="mb-2">
                Please install the Wander browser extension to connect your Arweave wallet.
              </p>
              <a
                href="https://wander.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-cyan-500 text-black font-medium rounded-lg glow-primary"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Wander
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass rounded-xl p-6 holo-border  ">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">
          Arweave Wallet
        </h2>
        <div className={`px-3 py-1 rounded-full text-sm font-medium  ${isConnected
            ? 'bg-green-500/20 text-green-400 border border-green-500/30'
            : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
          }`}>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-gray-400'}`}></div>
            <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 glass rounded-lg p-4 border border-red-500/30 glow-accent">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm text-red-300">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={clearError}
                className="text-red-400"
              >
                <span className="sr-only">Dismiss</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {isConnected && arweaveAddress ? (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Connected Address
            </label>
            <div className="glass rounded-lg p-4 border border-gray-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <code className="text-white font-mono text-sm bg-black/20 px-3 py-1 rounded">
                    {formatAddress(arweaveAddress)}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(arweaveAddress)}
                    className="text-gray-400 hover:text-white p-1 rounded transition-colors"
                    title="Copy address"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
                <button
                  onClick={handleDisconnect}
                  disabled={isLoading}
                  className="text-red-400 hover:text-red-300 px-3 py-1 text-sm font-medium transition-colors"
                >
                  Disconnect
                </button>
              </div>
            </div>
          </div>

          {showContinueButton && (
            <button
              onClick={onContinue}
              className="w-full bg-cyan-500 text-black py-3 px-6 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-cyan-500/50 glow-primary flex items-center justify-center"
            >
              <span>Continue</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      ) : (
        <div className="text-center">
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-cyan-400/20 rounded-full flex items-center justify-center border border-cyan-500/30 glow-primary">
              <svg className="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Connect your Wander wallet to link your EVM address
            </p>
          </div>

          <button
            onClick={handleConnect}
            disabled={isLoading}
            className="bg-cyan-500 text-black py-3 px-8 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-cyan-500/50 disabled:opacity-50 disabled:cursor-not-allowed glow-primary inline-flex items-center"
          >
            {isLoading ? (
              'Connecting...'
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                Connect Wander
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
